import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Check, X, Edit3, CheckCircle2, ExternalLink,
  Clock, AlertCircle, Loader2, Download, ChevronDown, ChevronRight,
} from "lucide-react";
import PointsRedeemer from "@/components/PointsRedeemer";
import { getToken } from "@/lib/session";

const STATUS_META = {
  draft:              { label: "Draft",             className: "bg-slate-100 text-slate-600" },
  sent:               { label: "Awaiting review",   className: "bg-amber-100 text-amber-700" },
  approved:           { label: "Approved · pay",    className: "bg-emerald-100 text-emerald-700" },
  revision_requested: { label: "Revision sent",     className: "bg-sky-100 text-sky-700" },
  denied:             { label: "Declined",          className: "bg-red-100 text-red-700" },
  paid:               { label: "Paid",              className: "bg-emerald-600 text-white" },
  open:               { label: "Awaiting review",   className: "bg-amber-100 text-amber-700" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.sent;
  return (
    <span
      data-testid={`invoice-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
}

function InvoicePdfViewer({ invoice, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const token = getToken();
  // Prefer uploaded PDF attachment; fall back to the branded backend PDF so
  // every invoice can be previewed inline without leaving the page.
  const url = invoice.pdf_file_id
    ? `/api/files/${invoice.pdf_file_id}${token ? `?auth=${encodeURIComponent(token)}` : ""}`
    : `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.invoice_id}/pdf${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  const filename = invoice.pdf_filename || `PowerBuilt-Invoice-${invoice.invoice_id}.pdf`;
  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden mt-4 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900/60 text-xs">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`pdf-toggle-${invoice.invoice_id}`}
          className="inline-flex items-center gap-1.5 font-medium text-[#0A2540] dark:text-white hover:text-[#C9A356] transition-colors"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <FileText className="w-3.5 h-3.5" />
          {filename}
          {!invoice.pdf_file_id && (
            <span className="ml-1 text-[9px] uppercase tracking-wider text-[#C9A356] font-bold">PowerBuilt PDF</span>
          )}
        </button>
        <div className="inline-flex items-center gap-2">
          <a
            href={url}
            download={filename}
            data-testid={`pdf-download-${invoice.invoice_id}`}
            className="inline-flex items-center gap-1 text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white transition-colors"
            title="Download to device"
          >
            <Download className="w-3 h-3" /> Download
          </a>
          <a
            href={url}
            target="_blank" rel="noreferrer"
            data-testid={`pdf-open-${invoice.invoice_id}`}
            className="inline-flex items-center gap-1 text-[#C9A356] hover:text-[#DDBD77] transition-colors"
            title="Open in new tab"
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
      {open && (
        <object
          data={url}
          type="application/pdf"
          className="w-full h-[520px] bg-slate-100 dark:bg-slate-900"
          data-testid={`pdf-viewer-${invoice.invoice_id}`}
        >
          <div className="p-6 text-xs text-[#475569] dark:text-slate-400 text-center">
            <FileText className="w-10 h-10 mx-auto text-[#94A3B8] mb-2" />
            <p>Your browser can't render PDFs inline.</p>
            <a href={url} target="_blank" rel="noreferrer" className="text-[#C9A356] underline mt-1 inline-block">
              Open the invoice in a new tab →
            </a>
          </div>
        </object>
      )}
    </div>
  );
}

function LineItemsPreview({ invoice }) {
  const items = invoice.line_items || [];
  if (items.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-900/60 text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
          <tr>
            <th className="text-left px-4 py-2">Description</th>
            <th className="text-right px-3 py-2 w-16">Qty</th>
            <th className="text-right px-3 py-2 w-24">Unit</th>
            <th className="text-right px-4 py-2 w-24">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => {
            const sub = (Number(li.qty) || 0) * (Number(li.unit_price) || 0);
            return (
              <tr key={`${li.description || "row"}-${i}`} className="border-t border-[#E2E8F0] dark:border-slate-700">
                <td className="px-4 py-2 text-[#0A2540] dark:text-white">{li.description || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{li.qty}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(li.unit_price)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtMoney(sub)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClientActionBar({ invoice, busyId, onApprove, onDeny, onRevision }) {
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [note, setNote] = useState("");
  const busy = busyId === invoice.invoice_id;

  if (invoice.status !== "sent") return null;

  return (
    <div className="mt-5 border-t border-[#E2E8F0] dark:border-slate-700 pt-5">
      {!showRevisionForm ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            onClick={() => onApprove(invoice)}
            disabled={busy}
            data-testid={`client-approve-${invoice.invoice_id}`}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm transition disabled:opacity-60"
          >
            <Check className="w-4 h-4" /> Approve
          </button>
          <button
            onClick={() => setShowRevisionForm(true)}
            disabled={busy}
            data-testid={`client-request-revision-${invoice.invoice_id}`}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 hover:border-sky-500 hover:text-sky-600 text-[#0A2540] dark:text-white font-semibold text-sm transition"
          >
            <Edit3 className="w-4 h-4" /> Request revision
          </button>
          <button
            onClick={() => onDeny(invoice)}
            disabled={busy}
            data-testid={`client-deny-${invoice.invoice_id}`}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 hover:border-red-500 hover:text-red-600 text-[#475569] dark:text-slate-300 font-semibold text-sm transition"
          >
            <X className="w-4 h-4" /> Decline
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-sky-200 dark:border-sky-800/50 bg-sky-50/70 dark:bg-sky-900/10 p-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-2">
            What would you like changed?
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Example: The panel count should be 12 not 10, and please split the labor into a separate line."
            data-testid={`revision-note-${invoice.invoice_id}`}
            maxLength={2000}
            className="w-full rounded-md border border-sky-200 dark:border-sky-700 dark:bg-slate-900 dark:text-white px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <span className="text-[10px] text-[#94A3B8]">{note.length}/2000</span>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowRevisionForm(false); setNote(""); }}
                className="h-9 px-3 text-sm text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!note.trim()) { toast.error("Please describe what to change"); return; }
                  onRevision(invoice, note.trim());
                  setShowRevisionForm(false);
                  setNote("");
                }}
                disabled={busy}
                data-testid={`submit-revision-${invoice.invoice_id}`}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-sm disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                Send revision request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [paying, setPaying] = useState(null);
  const [redemption, setRedemption] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/invoices/my");
      setInvoices(r.data);
    } catch (err) {
      console.error("Failed to load invoices:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to a focused invoice from a notification link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (focus && invoices.length > 0) {
      const el = document.querySelector(`[data-testid="invoice-row-${focus}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [invoices]);

  const decide = async (inv, decision, note) => {
    setBusyId(inv.invoice_id);
    try {
      await api.post(`/invoices/${inv.invoice_id}/client-decision`, { decision, note });
      const msg = {
        approve:  "Invoice approved — you can now pay.",
        deny:     "Invoice declined.",
        revision: "Revision request sent to PowerBuilt.",
      }[decision];
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const onApprove  = (inv) => decide(inv, "approve");
  const onDeny     = (inv) => {
    if (!window.confirm(`Decline ${inv.invoice_id}? PowerBuilt will be notified.`)) return;
    decide(inv, "deny");
  };
  const onRevision = (inv, note) => decide(inv, "revision", note);

  const downloadPrintable = (inv) => {
    // If admin attached a PDF, that's the canonical document — open it.
    if (inv.pdf_file_id) {
      const token = getToken();
      const url = `/api/files/${inv.pdf_file_id}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    // Otherwise open the branded backend-generated PDF — same look as
    // the admin download so clients receive a consistent PowerBuilt document.
    const token = getToken();
    const backendUrl =
      `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.invoice_id}/pdf` +
      (token ? `?auth=${encodeURIComponent(token)}` : "");
    window.open(backendUrl, "_blank", "noopener,noreferrer");
  };

  const pay = async (inv) => {
    setPaying(inv.invoice_id);
    try {
      const points = redemption[inv.invoice_id]?.points || 0;
      const r = await api.post("/payments/checkout", {
        package_type: "invoice",
        invoice_id:   inv.invoice_id,
        origin_url:   window.location.origin,
        redeem_points: points,
      });
      window.location.href = r.data.url;
    } catch (err) {
      console.error("Checkout start failed:", err);
      toast.error("Failed to start checkout");
      setPaying(null);
    }
  };

  const grouped = useMemo(() => {
    const g = { attention: [], approved: [], paid: [], other: [] };
    for (const inv of invoices) {
      if (inv.status === "sent") g.attention.push(inv);
      else if (inv.status === "approved") g.approved.push(inv);
      else if (inv.status === "paid") g.paid.push(inv);
      else g.other.push(inv);
    }
    return g;
  }, [invoices]);

  const renderInvoice = (inv) => {
    const r = redemption[inv.invoice_id] || { points: 0, discount: 0 };
    const total = Number(inv.grand_total ?? inv.amount ?? 0);
    const finalAmount = Math.max(total - r.discount, 0.5);
    const canPay = inv.status === "approved" || inv.status === "open";
    return (
      <article
        key={inv.invoice_id}
        data-testid={`invoice-row-${inv.invoice_id}`}
        className="card-pb p-5 sm:p-6"
      >
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-[#475569] dark:text-slate-400">#{inv.invoice_id}</span>
              <StatusPill status={inv.status} />
              {inv.is_overdue && inv.status !== "paid" && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  <AlertCircle className="w-3 h-3" /> Overdue
                </span>
              )}
            </div>
            <h3 className="mt-1 font-heading text-lg text-[#0A2540] dark:text-white">{inv.title || "Invoice"}</h3>
            {inv.due_date && (
              <p className="text-xs text-[#475569] dark:text-slate-400 mt-1 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Due {new Date(inv.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="text-right flex flex-col items-end gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Total</div>
              <div className="font-heading text-3xl font-bold text-[#0A2540] dark:text-white tabular-nums">
                {fmtMoney(finalAmount)}
              </div>
              {r.discount > 0 && !canPay && (
                <div className="text-xs text-[#94A3B8] line-through">{fmtMoney(total)}</div>
              )}
            </div>
            {inv.status !== "draft" && (
              <button
                onClick={() => downloadPrintable(inv)}
                data-testid={`download-printable-${inv.invoice_id}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A2540] dark:text-slate-300 hover:text-[#C9A356] dark:hover:text-[#C9A356] transition"
              >
                <Download className="w-3.5 h-3.5" /> Download printable copy
              </button>
            )}
          </div>
        </header>

        {/* Revision follow-up state */}
        {inv.status === "revision_requested" && inv.current_revision_note && (
          <div
            className="mt-4 rounded-lg border border-sky-200 dark:border-sky-800/40 bg-sky-50/60 dark:bg-sky-900/10 p-3"
            data-testid={`revision-pending-${inv.invoice_id}`}
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-1">
              Waiting on revision
            </p>
            <p className="text-sm text-[#0A2540] dark:text-white">{inv.current_revision_note}</p>
          </div>
        )}
        {inv.status === "denied" && inv.denial_note && (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-900/10 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 dark:text-red-300 mb-1">You declined this invoice</p>
            <p className="text-sm text-[#0A2540] dark:text-white">{inv.denial_note}</p>
          </div>
        )}

        {/* Content preview */}
        <LineItemsPreview invoice={inv} />
        <InvoicePdfViewer invoice={inv} defaultOpen={inv.status === "sent" || inv.status === "revision_requested"} />
        {inv.notes_client && (
          <p className="mt-3 text-sm text-[#475569] dark:text-slate-400 whitespace-pre-wrap">
            {inv.notes_client}
          </p>
        )}

        {/* Actions: client review OR pay */}
        <ClientActionBar
          invoice={inv}
          busyId={busyId}
          onApprove={onApprove}
          onDeny={onDeny}
          onRevision={onRevision}
        />

        {canPay && (
          <div className="mt-5 border-t border-[#E2E8F0] dark:border-slate-700 pt-5">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <PointsRedeemer
                  amount={total}
                  onChange={(points, discount) =>
                    setRedemption((s) => ({ ...s, [inv.invoice_id]: { points, discount } }))
                  }
                  testId={`invoice-${inv.invoice_id}-redeem`}
                />
              </div>
              <button
                onClick={() => pay(inv)}
                disabled={paying === inv.invoice_id}
                className="btn-accent w-full sm:w-56 h-11"
                data-testid={`pay-invoice-${inv.invoice_id}`}
              >
                {paying === inv.invoice_id ? "..." : `Pay ${fmtMoney(finalAmount)}`}
              </button>
            </div>
          </div>
        )}

        {inv.status === "paid" && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" /> Paid{inv.paid_at ? ` · ${new Date(inv.paid_at).toLocaleDateString()}` : ""}
          </div>
        )}
      </article>
    );
  };

  const totalAttention = grouped.attention.length;

  return (
    <div data-testid="my-invoices">
      <p className="overline">Billing</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-6 sm:mb-8">
        Invoices
      </h1>

      {invoices.length === 0 ? (
        <div className="card-pb p-12 text-center text-[#475569] dark:text-slate-400">No invoices yet</div>
      ) : (
        <div className="space-y-8">
          {totalAttention > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Needs your review</h2>
                <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {totalAttention}
                </span>
              </div>
              <div className="space-y-4">{grouped.attention.map(renderInvoice)}</div>
            </section>
          )}
          {grouped.approved.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Ready to pay</h2>
                <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  {grouped.approved.length}
                </span>
              </div>
              <div className="space-y-4">{grouped.approved.map(renderInvoice)}</div>
            </section>
          )}
          {grouped.other.length > 0 && (
            <section>
              <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">In progress</h2>
              <div className="space-y-4">{grouped.other.map(renderInvoice)}</div>
            </section>
          )}
          {grouped.paid.length > 0 && (
            <section>
              <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">History</h2>
              <div className="space-y-4">{grouped.paid.map(renderInvoice)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
